import { inject, Injectable } from '@angular/core';
import { Router, ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ExternalService, InternalService, InstanceDifferencesUserOptions } from '@finra/angular-data-collection-sdk';
import {
  AbstractInstanceService,
  AbstractInstanceServiceEnv,
  DataCollectionService,
  Instance,
  InstanceResolveData,
  mergeWithEmptyBaseline,
  OverlayService,
  setValue,
  SupportingData,
} from '@finra/rampdi-forms-common';
import { environment, Versions } from '../../environments/environment';
import { FipSessionService } from '@finra/fip-session';
import { catchError, concatMap, filter, first, map, mergeMap, tap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of, Subject, throwError, timer } from 'rxjs';
import { DisplayRulesService } from './display-rules.service';
import { AppHostUserInfo } from '../model/user.model';
import { ModUserProfile } from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { GlobalContext } from './global-context.service';
import { isJsonParsable } from '../utils/parseCheck.utils';
import { toTime } from '../utils/dateFormat.utils';
import { isDefined, isEmpty } from '@finra/forms';
import { WindowService } from './window.service';
import { HttpErrorResponse } from '@angular/common/http';

export interface MU3WithSupportingData extends SupportingData {
  modProfile: ModUserProfile;
  classification: string;
  userInfo: AppHostUserInfo;
}

export interface ExtensionConfig {}

export interface RFInstanceServiceEnv extends AbstractInstanceServiceEnv {
  extensions: {
    create: {
      name: string;
      namespace: string;
      version: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class InstanceService extends AbstractInstanceService<
  MU3WithSupportingData,
  Instance<any>,
  InstanceResolveData<MU3WithSupportingData, Instance<any>>
> {
  concurrencyDetected$ = new Subject<boolean>();
  clearingConcurrencyFlag = false;
  skipConcurrencyCheck = false;
  dataAll: any;
  displayRulesService = inject(DisplayRulesService);
  globalContext = inject(GlobalContext);
  lastSavedLicense: any;
  lastSavedCbcMethod: any;
  lastSavedCreditReport: any;
  isInstanceChange: boolean;
  viewType: string = '';
  lastSavedCreditRequestCR: any;
  constructor(
    es: ExternalService,
    is: InternalService,
    router: Router,
    route: ActivatedRoute,
    OS: OverlayService,
    DCS: DataCollectionService,
    private fss: FipSessionService,
    private windowService: WindowService,
  ) {
    super(es, is, router, route, OS, DCS);
  }

  // Overrided this method to resolve the multiple versions issue
  // Finforms are not supporting this and endpoints are removed.
  getInstance(id: any, version?: number): any {
    const versions = Object.values(environment.versions).reverse();

    return of(...versions).pipe(
      concatMap((element) =>
        this.es
          .getInstanceUsingGET(element.template.namespace, element.template.name, element.template.version, id, version)
          .pipe(
            map((i) => {
              const instance = { ...i, collectedData: i.collectedData ?? {} };
              this.sortEmploymentHistory(instance.collectedData);
              return instance;
            }),
            catchError(() => EMPTY), // Skip failed requests and continue to next version
          ),
      ),
      first(), // Take the first successful response and complete
    );
  }

  private sortEmploymentHistory(collectedData: any): void {
    if (!Array.isArray(collectedData?.employmentHistory)) return;

    collectedData.employmentHistory.sort((a, b) => {
      const aEnd = toTime(a?.employmentToDate);
      const bEnd = toTime(b?.employmentToDate);
      const aStart = toTime(a?.employmentFromDate);
      const bStart = toTime(b?.employmentFromDate);
      if (aEnd !== bEnd) return bEnd - aEnd;
      return bStart - aStart;
    });
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
    this.globalContext.getSession();
    if (route.data.create) {
      const { create } = this.environment.extensions;
      return this.es
        .invokeApiExtensionUsingPOST(
          create.name,
          create.version,
          create.namespace,
          {},
          // {
          //   firmId: +this.fss.userInformation?.nasdCRDFirmID[0],
          // }
        )
        .pipe(
          map((res: any) => {
            console.log('Extension call response', res);
            if (res.statusCode >= 400) {
              // lambda is returning 200 status code even when there are errors.
              // Need check inside the res to see if the response contains errors.
              return this.handleError(res.body, route);
            } else {
              this.OS.showSnackbar({
                theme: 'success',
                text: 'The MU3 Form has been created successfully',
              });
              this.router.navigate([''], {
                queryParamsHandling: 'merge',
                queryParams: {
                  instanceId: res.body.id,
                },
              });
              return res;
            }
          }),
          catchError((error) => {
            console.log('Extension call received error', error);
            return this.handleError(error, route);
          }),
        );
    } else {
      let retval;
      try {
        retval = super.resolve(route, state);
      } catch (error) {
        return this.handleError(error, route);
      }

      if (retval instanceof Observable) {
        return retval.pipe(
          catchError((error) => {
            return this.handleError(error, route);
          }),
        );
      } else {
        return retval;
      }
    }
  }

  handleError(error, route) {
    // Handle concurrency error - don't navigate to error page, just return
    if (error?.concurrencyIssue) {
      console.log('Concurrency issue detected, blocking save');
      return EMPTY;
    }

    let parsedError;
    console.log('HandleError received an error', error);
    parsedError = isJsonParsable(error) ? JSON.parse(error) : '';

    this.OS.showSnackbar({
      theme: 'error',
      text:
        'Failed to initialize MU3 Form' +
        (parsedError?.message ? ': ' + parsedError?.message : ': This MU3 form is no longer available.'),
    });
    this.router.navigate(['/error'], {
      queryParamsHandling: 'merge',
      queryParams: { ...route.queryParams },
      state: {
        error: JSON.stringify({ message: parsedError?.message, stack: parsedError?.stack }),
      },
    });
    console.info('parsed message', parsedError?.message);
    return error;
  }

  handleErrorResponse(error) {
    console.log('method received error resp', error);
    this.OS.showSnackbar({
      theme: 'error',
      text:
        'Failed to handle request. Error message : ' +
        (error.message
          ? error.message
          : 'Sorry, something went wrong. An unexpected error has occurred. Contact the NMLS Call Center (1-855-665-7123) for help.'),
    });
    return error;
  }

  handleSaveErrorResponse(error, errorMessage?: string) {
    console.log('method received error resp', error);
    this.OS.showSnackbar({
      theme: 'error',
      text:
        errorMessage ||
        'Failed to save MU3 form as the filing status has changed. The page will reload in a few seconds to reflect the current filing status.',
    });
    return error;
  }

  handleCloudWellErrorResponse() {
    console.log('method received error resp');
    this.OS.showSnackbar({
      theme: 'error',
      text: 'Agency-specific guidance is temporarily unavailable due to technical issues. Please try again later',
    });
  }

  handleSuccessResponse(successToastText) {
    console.log('method received success resp', successToastText);
    this.OS.showSnackbar({
      theme: 'success',
      text: successToastText,
    });
  }

  getInitialInstance() {
    return this.initialInstance;
  }

  getInstanceDiffOptions(instance, changesOnly: boolean, flattenResponse: boolean): InstanceDifferencesUserOptions {
    return {
      changesOnly,
      flattenResponse,
      collectionElementUniqueIds: {
        licenses: { 'decaf-keys': ['licenseTypeCode', 'regulatorCode'] },
      },
    };
  }

  getInitialInstanceDataAsEmpty() {
    return this.initialInstanceDataAsEmpty;
  }

  replaceEmptyStringOnEncryptedPropOnClear() {
    const individualInformation = this.currentInstance?.collectedData?.individualInformation;
    if (!individualInformation) return;
    const encryptedProperties = ['individualGovtIDIdentifier', 'individualPassportIdentifier'];
    encryptedProperties.forEach((encryptedProp) => {
      const individualInformationProp = individualInformation[encryptedProp];
      if (individualInformationProp && Object.keys(individualInformationProp).length > 0) {
        let isPropertyValueUndefined = false;
        Object.keys(individualInformationProp).forEach((key) => {
          if (key === 'value') {
            isPropertyValueUndefined = individualInformationProp[key] === undefined;
          }
        });
        if (isPropertyValueUndefined) {
          this.currentInstance.collectedData.individualInformation[encryptedProp].value = '';
        }
      }
    });
  }

  removeEmploymentGapPropOnSave(instance: any) {
    instance?.collectedData?.employmentHistory?.forEach((el) => {
      if (el.status === 'GAP') {
        el.address = null;
      }
    });
  }

  saveInstance(id: any, instance: any) {
    // Check for concurrency issue before saving
    return this.getInstance(id).pipe(
      concatMap((latestInstance: any) => {
        const latestHasFlag = latestInstance?.instanceMetadata?.attributes?.hasConcurrencyIssue;
        const instanceHasFlag = instance.instanceMetadata?.attributes?.hasConcurrencyIssue;

        // Only block if latest has flag AND instance doesn't have flag AND not clearing via Continue
        if (latestHasFlag && !instanceHasFlag && !this.clearingConcurrencyFlag && !this.skipConcurrencyCheck) {
          this.currentInstance.instanceMetadata.attributes['hasConcurrencyIssue'] = true;
          this.concurrencyDetected$.next(true);
          return throwError(() => ({ concurrencyIssue: true }));
        }

        // When clearing concurrency flag, use latest instance and apply the flag changes
        if (this.clearingConcurrencyFlag) {
          const concurrencySections: string[] = latestInstance?.instanceMetadata?.attributes?.concurrencySections || [];
          latestInstance.instanceMetadata.attributes['hasConcurrencyIssue'] = false;
          latestInstance.instanceMetadata.attributes['hasUnprocessedConcurrentFiling'] = false;
          latestInstance.instanceMetadata.attributes['baselineVersion'] = latestInstance.instanceVersion + 1;

          // If concurrencySections specifies sections (without FORM), only merge those sections
          // from the latest instance and keep the user's current data for everything else
          if (concurrencySections.length && !concurrencySections.includes('FORM')) {
            const sectionToDataKeys: Record<string, string[]> = {
              LICENSE: ['licenses'],
              DISCLOSURE: ['disclosureQuestions'],
              INDIVIDUAL_INFORMATION: ['individualInformation'],
              INDIVIDUAL_OTHER_BUSINESSES: ['individualOtherBusinesses'],
              DOCUMENT: ['documents'],
              CREDIT_REPORT: ['creditReportRequest'],
              CRIMINAL_BACKGROUND_CHECK: ['criminalBackgroundCheck'],
            };
            instance.instanceMetadata = latestInstance.instanceMetadata;
            instance.instanceVersion = latestInstance.instanceVersion;
            // Employment: only overwrite LINKED and COMPANYRELATIONSHIP entries, keep self-reported
            if (concurrencySections.includes('EMPLOYMENT')) {
              const latestLinked = (latestInstance.collectedData.employmentHistory || []).filter(
                (emp) => emp.status === 'LINKED' || emp.status === 'COMPANYRELATIONSHIP',
              );
              const latestById = new Map(latestLinked.map((emp) => [emp.id, emp]));
              // Replace matching entries in-place, remove stale linked entries
              instance.collectedData.employmentHistory = (instance.collectedData.employmentHistory || [])
                .map((emp) => {
                  if (emp.status === 'LINKED' || emp.status === 'COMPANYRELATIONSHIP') {
                    const updated = latestById.get(emp.id);
                    latestById.delete(emp.id);
                    return updated || null;
                  }
                  return emp;
                })
                .filter(Boolean)
                .concat([...latestById.values()]); // append any new linked entries from server
            }

            for (const section of concurrencySections) {
              const keys = sectionToDataKeys[section];
              if (keys) {
                for (const key of keys) {
                  instance.collectedData[key] = latestInstance.collectedData[key];
                }
              }
            }
          } else {
            this.currentInstance.instanceMetadata.attributes['concurrencySections'] = concurrencySections;
            instance = latestInstance;
          }
        }

        //Need to override merge method to prevent empty string values from being removed from payload of PATCH request.
        instance.collectedData = this.mergeWithEmptyBaseline(
          this.getInitialInstanceDataAsEmpty(),
          instance.collectedData,
        );
        //Employment gap doesn't have address info, remove defaulted address.countryCode
        this.removeEmploymentGapPropOnSave(instance);
        // Track highest disclosureExplanationNumber to prevent ID reuse after deletions
        const explanations = instance.collectedData?.disclosureQuestions?.disclosures?.disclosureExplanations || [];
        if (explanations.length) {
          instance.collectedData.disclosureQuestions.disclosureExplanationMaxId = Math.max(
            instance.collectedData.disclosureQuestions.disclosureExplanationMaxId || 0,
            ...explanations.map((e: any) => e.disclosureExplanationNumber || 0),
          );
        }
        globalThis.finformUnsavedChanges = false; //Clear unsaved changes flag as part of saving
        // note - remove cloudwell data before saving the instance
        if (instance.collectedData.cloudwell) {
          delete instance.collectedData.cloudwell;
        }
        // Remove read-only fields before saving
        delete instance.filingId;
        delete instance.instanceMetadata?.dates;
        delete instance.instanceMetadata?.userInfo;
        delete instance.instanceMetadata?.signatures;
        return this.es
          .partialUpdateInstanceUsingPATCH(
            instance.templateNamespace,
            instance.templateName,
            instance.templateVersion,
            id,
            instance,
          )
          .pipe(
            catchError((e) => {
              if (e.status === 403) {
                this.handleSaveErrorResponse(e);
                timer(5000).subscribe((r) => {
                  this.windowService.location.reload();
                });
                return EMPTY;
              }
              if (e.status === 409) {
                this.handleSaveErrorResponse(
                  e,
                  'Another user has updated this record. Refresh this page for the latest version',
                );
                timer(5000).subscribe((r) => {
                  this.windowService.location.reload();
                });
                return EMPTY;
              }
              return throwError(() => e);
            }),
          );
      }),
      catchError((e) => {
        if (e?.concurrencyIssue) {
          // Re-throw to be handled by caller, but don't reload
          return throwError(() => e);
        }
        return throwError(() => e);
      }),
    );
  }

  mergeWithEmptyBaseline(emptyBaseline, model) {
    const keys = Object.keys(model);
    const result = Array.isArray(model) ? [] : {};
    for (const key of keys) {
      const modelChild = model?.[key];
      const baseLineChild = emptyBaseline?.[key];

      if (this.isEmpty(modelChild)) {
        if (isDefined(baseLineChild)) {
          result[key] = Array.isArray(baseLineChild) ? [] : {};
        } else {
          result[key] = modelChild;
        }
      } else {
        if (typeof modelChild === 'object') {
          const mergedChild = this.mergeWithEmptyBaseline(baseLineChild, modelChild);

          if (this.isEmpty(mergedChild)) {
            if (isDefined(baseLineChild)) {
              result[key] = Array.isArray(baseLineChild) ? [] : {};
            }
          } else {
            result[key] = mergedChild;
          }
        } else {
          result[key] = modelChild;
        }
      }
    }

    return result;
  }

  isEmpty(obj, deepCheck = false) {
    //Overridden to not have check for empty string values.
    const empty = obj == undefined || obj == null || '[]' === JSON.stringify(obj) || '{}' === JSON.stringify(obj);
    if (!deepCheck) {
      return empty;
    } else {
      if (!empty) {
        if (typeof obj === 'object') {
          return Object.entries(obj).every(([k, v]) => isEmpty(v, true));
        } else {
          return false;
        }
      } else {
        return true;
      }
    }
  }

  get environment(): RFInstanceServiceEnv {
    const currentEnvVersion =
      environment.versions[(this.currentInstance?.templateVersion ?? environment.currentVersion) as Versions];
    return {
      formName: environment.formName,
      ...currentEnvVersion,
      extensions: {
        ...currentEnvVersion.extensions,
      },
    };
  }

  setdataforAll(data) {
    this.dataAll = data;
  }

  // this method always gets called before the form initializes, all async behavior should be resolved here
  getSupportingData(currentInstance) {
    const { classification, permissions, userInfo, modProfile } = this.globalContext.store();
    this.displayRulesService.setUserPermissions(modProfile, userInfo, classification, permissions, {
      ...currentInstance?.instanceMetadata,
      templateVersion: currentInstance?.templateVersion,
    });
    return of({
      classification,
      userInfo,
      modProfile,
      isWebCRDDown: false,
    });
  }

  public addDataChangesToInstance(data, modelPath: string[] = []) {
    if (
      (data?.licenses && data?.licenses?.length != this.currentInstance?.collectedData?.licenses) ||
      (data?.cbcMethod &&
        data?.cbcMethod !== this.currentInstance?.collectedData?.criminalBackgroundCheck?.cbcMethod) ||
      (data?.existingCreditReport &&
        data?.existingCreditReport !== this.currentInstance?.collectedData?.creditReportRequest?.existingCreditReport)
    ) {
      this.lastSavedLicense = this.currentInstance?.collectedData?.licenses
        ? [...this.currentInstance.collectedData.licenses]
        : [];
      this.lastSavedCbcMethod = this.currentInstance?.collectedData?.criminalBackgroundCheck?.cbcMethod;
      this.lastSavedCreditReport = this.currentInstance?.collectedData?.creditReportRequest?.existingCreditReport;
      this.lastSavedCreditRequestCR = this.currentInstance?.collectedData?.creditReportRequest?.requestCR;
    }
    const instance = this.currentInstance;
    setValue(instance, ['collectedData'].concat(modelPath), data);
    this.currentInstance = instance;
  }

  recallFiling(): Observable<any> {
    const { templateNamespace, templateName, templateVersion, id, instanceMetadata } = this.currentInstance;
    const recallPatch = { instanceMetadata: { status: 'DRAFT', attributes: { attestationStatus: 'NONE' } } } as any;
    return instanceMetadata?.signatures
      ? this.es
          .unSetInstanceSignatureUsingDELETE(
            templateNamespace,
            templateName,
            templateVersion,
            id,
            'indvlAcknowledgementConsent',
          )
          .pipe(
            filter((res) => !!res),
            concatMap(() =>
              this.es.partialUpdateInstanceUsingPATCH(
                templateNamespace,
                templateName,
                templateVersion,
                id,
                recallPatch,
              ),
            ),
          )
      : this.es.partialUpdateInstanceUsingPATCH(templateNamespace, templateName, templateVersion, id, recallPatch);
  }
}
